import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Panel } from '@/components/forge/Panel'
import { PlainBadge, hueForSeverity } from '@/components/forge/StatusBadge'
import { Eyebrow, Mono, Num, Serial } from '@/components/forge/primitives'
import { formatDateTime, formatTime } from '@/lib/format'
import { cn } from '@/lib/cn'

/**
 * The same entries, read as a run of time. LIVE.
 *
 * ── Why it is a second view and not a second screen ───────────────────────
 * The feed answers "what has the crew been writing"; this answers "what
 * happened on Tuesday night". Same rows, same order, same filters — only the
 * density and the day breaks differ, which is why the toggle sits in the URL
 * and nothing refetches when it moves.
 *
 * ── Grouped by SHIFT DATE, not by the day it was typed ───────────────────
 * `shift_date` is the day the work happened and `created_at` is when somebody
 * got round to writing it up. They are the same on most rows and differ exactly
 * where it matters — a night shift written up at 00:40 belongs under the day it
 * started. Grouping on `created_at` would split one night across two headings.
 *
 * ── The dot takes the SEVERITY's hue, where it used to take the shift's ──
 * The demo coloured each dot by the shift somebody worked, from the roster's
 * own three-value map. The live `shift_id` is a PATTERN from Shift Master and
 * the entry does not carry its type, so that map would need a second request
 * per row to apply and would still be wrong for three of `ShiftType`'s six
 * members. Severity is on the row, is the thing a reader scanning a night is
 * actually looking for, and uses the hue the rest of the product already
 * assigns it.
 */
export function LogbookTimeline({ entries }) {
  const { t } = useTranslation(['app', 'common'])

  /* Grouped in render order rather than sorted into a map first: the rows
     arrive newest-first from the server and must stay that way, and a plain
     object would reorder date keys that look like numbers. */
  const days = []
  for (const entry of entries) {
    const last = days[days.length - 1]
    if (last && last.date === entry.shiftDate) last.entries.push(entry)
    else days.push({ date: entry.shiftDate, entries: [entry] })
  }

  return (
    <Panel className="flex flex-col gap-5">
      {days.map((day) => (
        <section key={day.date}>
          <Eyebrow as="h2" className="mb-2.5">
            <Num>{formatDateTime(day.date, { weekday: 'long', day: 'numeric', month: 'long' })}</Num>
          </Eyebrow>

          <ol className="relative m-0 list-none p-0 ps-4">
            {/* The rule. `start-[3px]` and not `left` — logical properties
                only, so it runs down the other edge under RTL. */}
            <span
              aria-hidden="true"
              className="absolute start-[3px] top-1.5 bottom-1.5 w-px bg-hairline"
            />

            {day.entries.map((entry) => {
              const hue = hueForSeverity(entry.severity)

              return (
                <li key={entry.id} className="relative pb-4 last:pb-0">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute top-1.5 size-[9px] rounded-full ring-2 ring-card',
                      // A severity outside the four is not a thing the API can
                      // send, but a null hue must still draw something rather
                      // than an invisible dot on a line of dots.
                      !hue && 'bg-ink-mute',
                    )}
                    style={{
                      insetInlineStart: '-13px',
                      backgroundColor: hue ? `var(--hue-${hue})` : undefined,
                    }}
                  />

                  <div className="flex flex-wrap items-center gap-2">
                    <Mono className="text-body-sm font-semibold">
                      {/* The wall clock of the WRITE. It is the only time on the
                          row — `shift_date` has no hour — so it is what orders
                          the entries within a day. */}
                      <Num>{formatTime(entry.createdAt)}</Num>
                    </Mono>
                    <Link
                      to={`/app/logbook/${entry.id}`}
                      className="min-w-0 flex-1 truncate text-body-sm text-ink no-underline hover:text-on-forge"
                    >
                      {entry.title}
                    </Link>
                    <PlainBadge>{t(`logbook.category.${entry.category}`)}</PlainBadge>
                    <Serial value={entry.serialNumber} className="text-micro text-ink-dim" />
                  </div>

                  <p className="m-0 mt-0.5 text-micro font-normal text-ink-dim">
                    {[entry.hierarchy.machine, entry.hierarchy.line, entry.hierarchy.plant]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </li>
              )
            })}
          </ol>
        </section>
      ))}
    </Panel>
  )
}
